import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Container from "../../../Layouts/Container";
import {
  getReportDateRange,
  formatReportDate,
} from "../../../Wrapper/ReportDateUtils";
import csvIcon from "../../../assets/img/csvicon.svg";
import DownloadIcon from "../../../assets/img/downloadIcon.svg";
import pdfIcon from "../../../assets/img/pdficon.svg";
import xlsIcon from "../../../assets/img/xlsicon.svg";
import { useFetch } from "../../../hooks/useFetch";
import useAbacAccess from "../../../hooks/useAbacAccess";
import { useDownloadData } from "../../../hooks/usePaginationNDownload";
import { getBrowserGMTOffset, hasRoles, showToast } from "../../../lib/utility";
import { middleware } from "../../../redux/store";
import { authSelector } from "../../Auth/AuthSlice";
import CloseButton from "../../Common/CloseButton";
import CommonHeader from "../../Common/CommonHeader";
import CustomSelect from "../../Common/CustomSelect";
import Modal from "../../Common/Modal";
import TableWithHeader from "../../Common/TableWithHeader";
import SubmissionTable from "../DataTable/SubmissionTable";
import SubmissionFilter from "../ReportFilterModal/SubmissionFilter";
import useOpenFilterModalWithLoader from "../useOpenFilterModalWithLoader";
import useReportMountReset from "../useReportMountReset";
import {
  clearStateSubmissionReport,
  getSavedFilter,
  reportSelector,
  submissionUserData,
} from "../ReportSlice";

const getDefaultFilterData = (bodyForDate) => ({
  enddate: formatReportDate(bodyForDate.endDate),
  startdate: formatReportDate(bodyForDate.startDate),
  reportType: null,
  country: "%",
  operator: "%",
  toGmt: getBrowserGMTOffset(),
});

const buildSaveFilterAttributes = (filterData, user) => ({
  reportType: filterData.reportType ?? null,
  clientId: filterData.clientId || user.username,
  toGmt: filterData.toGmt || getBrowserGMTOffset(),
  startdate: filterData.startdate || filterData.startDate,
  enddate: filterData.enddate || filterData.endDate,
  status: filterData.messageStatus || "%",
  senderId: filterData.senderId || "",
  country: filterData.country || "%",
});

const SubmissionReport = ({ modal, toggleModal, isSeller = false }) => {
  const { t } = useTranslation("common");
  const { t: tSummary } = useTranslation("summaryDelivery");
  const reportName = t("report.submissionReport.title", {
    defaultValue: "Submission Report",
  });
  let bodyForDate = getReportDateRange(10);

  const { canAccessNavbar } = useAbacAccess();
  const canViewReport = canAccessNavbar("submission_report");
  const { showFilterModal, setShowFilterModal, showInitialLoader } =
    useOpenFilterModalWithLoader(true, canViewReport);
  const [searchVal, setSearchVal] = useState("");
  const [savedFilterList, setSavedFilterList] = useState([]);
  const [filterData, setFilterData] = useState(() =>
    getDefaultFilterData(bodyForDate)
  );

  const downloadTypes = [
    { value: "csv", label: "CSV", image: csvIcon },
    { value: "pdf", label: "PDF", image: pdfIcon },
    { value: "xlsx", label: "XLSX", image: xlsIcon },
  ];
  
  const dispatch = useDispatch();
  const { submissionUser, errorMessageSubmissionUser, isFetching } =
    useSelector(reportSelector);
  const { user } = useSelector(authSelector);

  const submissionData = useMemo(() => {
    if (Array.isArray(submissionUser)) return submissionUser;
    if (Array.isArray(submissionUser?.data)) return submissionUser.data;
    return [];
  }, [submissionUser]);

  const {
    fileType,
    pagination,
    setFileType,
    downloadContent,
    handlePageChange,
    handlePerPageChange,
  } = useDownloadData(submissionData, {
    reportName,
    paginationDefaultSize: 10,
    mappable: (data) => {
      if (!data || !Array.isArray(data)) return [];
      
      return data.map((row) => ({
        [tSummary("submissionReport.table.columns.date", {
          defaultValue: "Date",
        })]: row.startDate ?? "-",
        [tSummary("submissionReport.table.columns.count", {
          defaultValue: "Count",
        })]: row.totalSmsSend ?? 0,
        ...(filterData.country && filterData.country !== "%"
          ? {
              [tSummary("submissionReport.table.columns.country", {
                defaultValue: "Country",
              })]: row.country,
            }
          : {}),
        ...(filterData.status && filterData.status !== "%"
          ? {
              [tSummary("submissionReport.table.columns.status", {
                defaultValue: "Status",
              })]: row.messageStatus,
            }
          : {}),
      }));
    },
  });

  const resetReportLocalState = useCallback(
    (resetFilterData) => {
      setSearchVal("");
      setFilterData(resetFilterData ?? getDefaultFilterData(bodyForDate));
    },
    [bodyForDate]
  );

  useReportMountReset(clearStateSubmissionReport, () =>
    resetReportLocalState()
  );

  const handleApplyFilter = useCallback((obj, searchValue) => {
    const reportType =
      obj.reportType !== undefined ? obj.reportType : obj.time ?? null;

    let filterObj = {
      messageStatus: obj.status !== "%" ? obj.status : undefined,
      senderId: obj.senderId !== "" ? obj.senderId : undefined,
      country: obj.country !== "%" ? obj.country : undefined,
      reportType,
    };

    if (obj.clientId) {
      filterObj.clientId = obj.clientId;
    }

    if (obj.toGmt) {
      filterObj.toGmt = obj.toGmt;
    }

    if (reportType === null || reportType === undefined) {
      let startDate = obj.startdate || obj.startDate;
      let endDate = obj.enddate || obj.endDate;

      if (startDate && endDate) {
        const isFormatted = (date) => {
          return (
            typeof date === "string" &&
            date.includes(" ") &&
            date.includes(":") &&
            date.includes(".")
          );
        };

        if (isFormatted(startDate)) {
          filterObj.startdate = startDate;
        } else {
          filterObj.startdate = formatReportDate(startDate, false);
        }

        if (isFormatted(endDate)) {
          filterObj.enddate = endDate;
        } else {
          filterObj.enddate = formatReportDate(endDate, true);
        }
      } else {
        let bodyForDate = getReportDateRange(10);
        filterObj.startdate = formatReportDate(bodyForDate.startDate, false);
        filterObj.enddate = formatReportDate(bodyForDate.endDate, true);
      }
    } else if (reportType === "Monthly") {
      const month = obj.month || (obj.startdate || obj.startDate || "").split(" ")[0]?.slice(0, 7);
      if (month) {
        filterObj.startdate = month + "-01 00:00:00.000";
        filterObj.enddate = "2020-01-01 00:00:00.000";
      }
    } else if (reportType === "Daily") {
      const date = obj.date || (obj.startdate || obj.startDate || "").split(" ")[0];
      if (date) {
        filterObj.startdate = date + " 00:00:00.000";
        filterObj.enddate = "2020-01-01 00:00:00.000";
      }
    }

    setSearchVal(searchValue ?? "");
    setFilterData(filterObj);
    getSubmissionDataOnce(filterObj, searchValue ?? "");
  }, [filterData, user]);

  function getSubmissionDataOnce(obj, search = "") {
    dispatch(
      submissionUserData({
        ...(hasRoles("admin,superadmin,system,seller,manager", user)
          ? { clientId: "%" }
          : {}),
        ...filterData,
        ...obj,
        search,
      })
    ).then((action) =>
      middleware(action, reportName)
    );
  }

  const handleFileTypeChange = useCallback((e) => {
    setFileType(e);
  }, [setFileType]);

  const handleSavedFilterClick = useCallback((values) => {
    setSearchVal(values.searchValue ?? "");

    const reportType =
      values.reportType !== undefined ? values.reportType : values.time ?? null;
    const startdate = values.startdate || values.startDate;
    const enddate = values.enddate || values.endDate;

    const processedValues = {
      status: values.status || "%",
      senderId: values.senderId || "",
      country: values.country || "%",
      reportType,
      time: reportType,
      date:
        values.date ||
        (reportType === "Daily" && startdate ? startdate.split(" ")[0] : ""),
      month:
        values.month ||
        (reportType === "Monthly" && startdate
          ? startdate.split(" ")[0]?.slice(0, 7)
          : ""),
      clientId: values.clientId || "%",
      toGmt: values.toGmt || getBrowserGMTOffset(),
      startdate,
      enddate,
    };

    handleApplyFilter(processedValues, values.searchValue);
  }, [handleApplyFilter]);

  const getSavedFiltersList = useCallback(() => {
    dispatch(getSavedFilter("SUBMISSION")).then((action) =>
      middleware(action, "Saved Filter", () => {
        setSavedFilterList(action.payload.data);
      })
    );
  }, [dispatch]);

  useFetch(() => {
    getSavedFiltersList();
  });

  const handleFilterModalClose = useCallback(() => {
    setShowFilterModal(false);
    getSavedFiltersList();
  }, [getSavedFiltersList]);

  const closeButtonElement = useMemo(
    () => !isSeller && <CloseButton onClick={toggleModal} theme="dark" />,
    [isSeller, toggleModal]
  );

  const searchConfig = useMemo(
    () => ({
      searchVal,
      setSearchVal,
      saveFilterKey: "SUBMISSION",
      filterData: buildSaveFilterAttributes(filterData, user),
      refreshFilter: getSavedFiltersList,
    }),
    [searchVal, filterData, user, getSavedFiltersList]
  );

  return isSeller ? (
    <Container className="submission-page-contents">
      <CommonHeader
        name={t("report.submissionReport.title", {
          defaultValue: "Submission Report",
        })}
        fileType={fileType}
        handleFileTypeChange={handleFileTypeChange}
        downloadContent={downloadContent}
        filterData={
          filterData?.startdate && filterData?.enddate
            ? {
                startDate: filterData.startdate,
                endDate: filterData.enddate,
              }
            : undefined
        }
        timeSeperator=" "
      />
      <TableWithHeader
        className="submission-list-section"
        name={t("report.submissionReport.tableName", { defaultValue: "Submission" })}
        search={searchConfig}
        serverSearch={false}
        setShowFilterModal={setShowFilterModal}
        showSavedFilter={savedFilterList}
        handleSavedFilterClick={handleSavedFilterClick}
        customChildren={closeButtonElement}
        customChildrenPlacement={"last"}
      >
        <div className="bg-white submission-list-body">
          <SubmissionTable
            submissionTableData={submissionData}
            isFetching={isFetching}
            country={filterData.country}
            status={filterData.messageStatus}
            searchVal={searchVal}
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePerPageChange={handlePerPageChange}
          />
        </div>
      </TableWithHeader>
      {showFilterModal && (
        <SubmissionFilter
          modal={showFilterModal}
          closeModal={handleFilterModalClose}
          onApplyFilter={handleApplyFilter}
          canSelectUsers
          initialFilterData={filterData}
        />
      )}
    </Container>
  ) : (
    <Modal justify="center" width={950} show={modal} close={toggleModal}>
      <TableWithHeader
        className="submission-list-section"
        name={t("report.submissionReport.tableName", { defaultValue: "Submission" })}
        search={searchConfig}
        serverSearch={false}
        setShowFilterModal={setShowFilterModal}
        showSavedFilter={savedFilterList}
        handleSavedFilterClick={handleSavedFilterClick}
        customChildren={closeButtonElement}
        customChildrenPlacement={"last"}
      >
        <div className="bg-white submission-list-body">
          {!isSeller && (
            <div className="d-flex justify-content-end m-3">
              <div className="d-flex dwn-btn-border">
                <div
                  style={{
                    position: "relative",
                    zIndex: 5,
                    borderRight: "1px solid #b5b5b5",
                    minWidth: 70,
                  }}
                >
                  <CustomSelect
                    selectData={{
                      fileType,
                      handleFileTypeChange,
                      downloadTypes,
                    }}
                  />
                </div>
                <button
                type="button"
                className="btn dwn-btn"
                onClick={downloadContent}
              >
                <span>{t("commonUi.download", { defaultValue: "Download" })}</span>
                  <img src={DownloadIcon} alt="" loading="lazy" className="ms-2" />
                </button>
              </div>
            </div>
          )}
          <SubmissionTable
            submissionTableData={submissionData}
            isFetching={isFetching}
            country={filterData.country}
            status={filterData.messageStatus}
            searchVal={searchVal}
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePerPageChange={handlePerPageChange}
          />
        </div>
      </TableWithHeader>
      {showFilterModal && (
        <SubmissionFilter
          modal={showFilterModal}
          closeModal={handleFilterModalClose}
          onApplyFilter={handleApplyFilter}
          canSelectUsers
          initialFilterData={filterData}
        />
      )}
    </Modal>
  );
};

export default SubmissionReport;